/*
curve — the pure model behind curve-field's `curve3d`: a continuous function
from [0,1] to itself, as sorted control points, with the editing rules that keep
it valid. Babylon-free, DOM-free, unit-tested. The reader-facing rules (closed
range, profile vs falloff, not monotonic, a drag can reorder) are on the
curve-field page under "The model".

The closed range is load-bearing: amplitude belongs to the block and shape to
the curve, so carve/patch-field and the heightfield agree about where a
province is. Non-monotonic falloffs (rims, cones) are the cases
PROVINCE-DESIGN.md says break the shared falloff deliberately.
*/

import { PiecewiseLinearFilter, type ControlPoint } from './gradient-filter.js'

export type { ControlPoint }

/**
 * What the curve means, which is what decides its endpoint rules.
 *
 * - `profile` — remaps a value. Both ends free.
 * - `falloff` — weight against normalised distance. Pinned to 0 at x = 1.
 * - `radial` — the FOOTPRINT: extent in each direction, x being angle over a
 *   full turn. **Periodic**, so the two ends are the same point.
 */
export type CurveKind = 'profile' | 'falloff' | 'radial'

const clamp01 = (n: number): number =>
  !Number.isFinite(n) ? 0 : n < 0 ? 0 : n > 1 ? 1 : n

/**
 * Put a point list into a state the rest of the module can rely on: sorted by x,
 * inside the unit square, spanning the full domain, and obeying the kind's
 * endpoint rule.
 *
 * Every mutating helper runs through this, so there is one definition of "valid"
 * rather than one per operation.
 */
export function normalizeCurve(
  points: ControlPoint[],
  kind: CurveKind = 'profile'
): ControlPoint[] {
  const out = points
    .filter((p) => p != null && Number.isFinite(p.x) && Number.isFinite(p.y))
    .map((p) => ({ x: clamp01(p.x), y: clamp01(p.y) }))
    .sort((a, b) => a.x - b.x)

  if (out.length === 0) return kind === 'falloff' ? falloffDefault() : linear()
  // The domain must be covered, or `evaluate` holds the end value flat over a
  // stretch nobody authored — which looks like a bug in the curve rather than
  // a missing point.
  if (out[0].x > 0) out.unshift({ x: 0, y: out[0].y })
  if (out[out.length - 1].x < 1) out.push({ x: 1, y: out[out.length - 1].y })
  if (kind === 'falloff') out[out.length - 1] = { x: 1, y: 0 }
  /*
  A radial curve WRAPS: x = 0 and x = 1 are the same direction, one full turn
  apart. If the ends disagree the footprint has a discontinuity along the +x
  axis — a slice out of the province at exactly one bearing, which reads as a
  crack rather than as a curve that needed pinning.

  So the last point is not a free point at all; it is a readout of the first.
  */
  if (kind === 'radial' && out.length > 1) {
    out[out.length - 1] = { x: 1, y: out[0].y }
  }
  return out
}

/**
 * Compose a base sample with a province's, by the province's weight.
 *
 * **This is the whole reason the range is closed.** A convex combination of two
 * values in `[0,1]`, by a weight in `[0,1]`, is in `[0,1]` — so a tile's bounds
 * are known BEFORE anything is evaluated, however many provinces overlap and
 * whatever curves they carry. That is what [[carve]] and [[patch-field]] need:
 * not "the terrain is usually about this tall", but a height a bore can be
 * authored against.
 *
 * Amplitude is then a single multiply on the composed result — it scales the
 * whole block, and cannot push one province through the top of it. Tonio, on a
 * first draft of the province demo that got this backwards: _"shouldn't height
 * just scale the whole terrain tile? If it pulls polygons outside the bounds
 * then it doesn't play nicely with carving, does it?"_ It does not.
 *
 * Inputs are clamped rather than trusted, because the guarantee is worth more
 * than the caller's arithmetic.
 */
export function blendSample(
  base: number,
  province: number,
  weight: number
): number {
  const w = clamp01(weight)
  return clamp01(base) * (1 - w) + clamp01(province) * w
}

/** Sample the curve. Input clamped, so a caller cannot read off the ends. */
export function evaluateCurve(points: ControlPoint[], t: number): number {
  return new PiecewiseLinearFilter(points.map((p) => ({ ...p }))).evaluate(t)
}

/**
 * Move a point, clamped into the unit square and obeying the kind's rules.
 *
 * Returns the point's NEW index: dragging past a neighbour reorders, and a
 * caller that keeps the old index silently grabs a different point mid-drag.
 */
export function movePoint(
  points: ControlPoint[],
  index: number,
  x: number,
  y: number,
  kind: CurveKind = 'profile'
): { points: ControlPoint[]; index: number } {
  if (index < 0 || index >= points.length) return { points, index }
  const last = points.length - 1
  const moved = { x: clamp01(x), y: clamp01(y) }

  // The end points own the domain edges — dragging one inward would leave the
  // curve undefined out to the boundary, which `normalizeCurve` would then
  // "fix" by inventing a point, fighting the drag.
  if (index === 0) moved.x = 0
  if (index === last) moved.x = 1
  if (kind === 'falloff' && index === last) moved.y = 0

  const next = points.map((p, i) => (i === index ? moved : { ...p }))
  // Dragging either end of a PERIODIC curve moves both — they are one point seen
  // twice, and letting them diverge is how the seam gets in.
  if (kind === 'radial' && (index === 0 || index === last)) {
    next[0] = { x: 0, y: moved.y }
    next[next.length - 1] = { x: 1, y: moved.y }
  }
  // Identity by reference, so the index survives the sort exactly.
  const target = next[index]
  next.sort((a, b) => a.x - b.x)
  return { points: normalizeCurve(next, kind), index: next.indexOf(target) }
}

/** Add a point. Returns its index so a caller can drag it immediately. */
export function insertPoint(
  points: ControlPoint[],
  x: number,
  y: number,
  kind: CurveKind = 'profile'
): { points: ControlPoint[]; index: number } {
  const p = { x: clamp01(x), y: clamp01(y) }
  const next = [...points.map((q) => ({ ...q })), p]
  next.sort((a, b) => a.x - b.x)
  return { points: normalizeCurve(next, kind), index: next.indexOf(p) }
}

/**
 * Remove a point. The two ENDS cannot be removed and the curve keeps at least
 * two points — a curve with one point is a constant that no longer says what it
 * is constant across.
 */
export function deletePoint(
  points: ControlPoint[],
  index: number,
  kind: CurveKind = 'profile'
): ControlPoint[] {
  if (points.length <= 2) return points
  if (index <= 0 || index >= points.length - 1) return points
  return normalizeCurve(
    points.filter((_, i) => i !== index),
    kind
  )
}

/** Index of the point within `radius` of (x, y), nearest first, or -1. */
export function pointAt(
  points: ControlPoint[],
  x: number,
  y: number,
  radius: number
): number {
  let best = -1
  let bestD = radius * radius
  points.forEach((p, i) => {
    const dx = p.x - x
    const dy = p.y - y
    const d = dx * dx + dy * dy
    if (d <= bestD) {
      bestD = d
      best = i
    }
  })
  return best
}

/* ------------------------------------------------------- footprint polygons */

/**
 * Closest a vertex may come to the centre.
 *
 * Not a taste: with every radius positive and the angles monotonic over one
 * turn, the polygon is **star-shaped about its centre**, which is what makes
 * "normalised distance in this direction" well defined at all. A vertex at the
 * origin collapses two edges onto each other and the direction lookup stops
 * having an answer.
 */
export const MIN_EXTENT = 0.05

/**
 * Smallest gap between two split markers, in curve x.
 *
 * Not zero: two markers at the same place make a segment of zero width, which
 * is a region you can never see, edit or get back — the marker under it becomes
 * ungrabbable because its neighbour is exactly on top of it.
 */
export const MIN_SPLIT_GAP = 0.02

/**
 * Move split marker `i` to `x`, keeping the set ascending and inside `[0,1]`.
 *
 * Markers divide ONE curve into regions — a light's attack / sustain / decay
 * ([[light-modulation]]) is the first user. They are clamped rather than
 * refused: a drag that stops at its neighbour shows you the limit, where one
 * that ignores you looks broken. Same rule as a footprint vertex.
 *
 * Returns a new array; the input is untouched.
 */
export function moveMarker(
  markers: number[],
  i: number,
  x: number,
  minGap = MIN_SPLIT_GAP
): number[] {
  if (i < 0 || i >= markers.length) return [...markers]
  const lo = i === 0 ? 0 : markers[i - 1] + minGap
  const hi = i === markers.length - 1 ? 1 : markers[i + 1] - minGap
  const next = [...markers]
  // `lo` can exceed `hi` when neighbours are already tighter than two gaps
  // apart. Clamping high-then-low leaves the marker pinned against its lower
  // neighbour rather than jumping past it.
  next[i] = Math.max(0, Math.min(1, Math.min(hi, Math.max(lo, x))))
  return next
}

/** Force a marker set ascending and in range — for values arriving from outside. */
export function normalizeMarkers(
  markers: number[],
  minGap = MIN_SPLIT_GAP
): number[] {
  const out = [...markers]
    .map((v) => Math.max(0, Math.min(1, Number.isFinite(v) ? v : 0)))
    .sort((a, b) => a - b)
  for (let i = 1; i < out.length; i++) {
    if (out[i] < out[i - 1] + minGap) out[i] = out[i - 1] + minGap
  }
  // A set too wide for [0,1] gets pushed back from the top, which can violate
  // the lower bound again — accept the squeeze rather than returning something
  // out of range.
  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] > 1) out[i] = 1
    if (i > 0 && out[i] - out[i - 1] < minGap) {
      out[i - 1] = Math.max(0, out[i] - minGap)
    }
  }
  return out
}

/** Smallest angular gap between neighbouring vertices, as a fraction of a turn. */
const MIN_GAP = 0.005

/** The closed radial curve as N distinct vertices — the last point is the first. */
export function polygonVertices(points: ControlPoint[]): ControlPoint[] {
  const c = normalizeCurve(points, 'radial')
  return c.slice(0, -1).map((p) => ({ ...p }))
}

/** N vertices as a closed radial curve, ready to `evaluateCurve`. */
export function closePolygon(vertices: ControlPoint[]): ControlPoint[] {
  return normalizeCurve(vertices, 'radial')
}

/**
 * Move one vertex, keeping the polygon valid.
 *
 * Tonio: _"you can't move a point to make an edge degenerate (i.e. the ngon has
 * to have points monotonic in theta and enclose the center)."_ Both are clamps
 * rather than rejections — a drag that stops at the limit tells you where the
 * limit is, whereas a drag that refuses to move looks broken.
 *
 * The angle is clamped **between its neighbours the short way round**, so a
 * vertex can slide anywhere in its own gap and never through one.
 *
 * It returns the vertex's NEW index, because the list is a CYCLIC sequence being
 * stored in a linear array: vertex 0 dragged backwards past x = 0 is a perfectly
 * legal move that lands it at the far end. Keeping the array canonically sorted
 * and reporting where the point went beats leaving it unsorted — a sweep over
 * every vertex and a spread of angles found exactly this, and an unsorted
 * footprint breaks the direction lookup that is the whole point of it.
 */
export function moveVertex(
  vertices: ControlPoint[],
  index: number,
  theta: number,
  r: number
): { vertices: ControlPoint[]; index: number } {
  const n = vertices.length
  if (n < 3 || index < 0 || index >= n) return { vertices, index }
  const turn = (t: number): number => ((t % 1) + 1) % 1
  const prev = vertices[(index - 1 + n) % n].x
  const next = vertices[(index + 1) % n].x
  // Everything measured as a distance FORWARD from the previous vertex, so the
  // wrap at x = 1 needs no special case.
  const gap = turn(next - prev) || 1
  const want = turn(turn(theta) - prev)
  const lo = MIN_GAP
  const hi = Math.max(lo, gap - MIN_GAP)
  const t = Math.min(hi, Math.max(lo, want))
  const next_ = vertices.map((v, i) =>
    i === index
      ? { x: turn(prev + t), y: Math.max(MIN_EXTENT, clamp01(r)) }
      : { ...v }
  )
  const target = next_[index]
  next_.sort((a, b) => a.x - b.x)
  return { vertices: next_, index: next_.indexOf(target) }
}

/** Does every vertex clear the centre, with angles in order? */
export function isStarShaped(vertices: ControlPoint[]): boolean {
  if (vertices.length < 3) return false
  if (!vertices.every((v) => v.y >= MIN_EXTENT && v.y <= 1)) return false
  return vertices.every((v, i) => i === 0 || vertices[i - 1].x < v.x)
}

/* ------------------------------------------------------------------ presets */

/** Sample a continuous easing into control points a piecewise-linear can hold. */
function sampled(f: (t: number) => number, steps = 8): ControlPoint[] {
  const out: ControlPoint[] = []
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    out.push({ x: t, y: clamp01(f(t)) })
  }
  return out
}

/** The identity: 0 → 0, 1 → 1. */
export function linear(): ControlPoint[] {
  return [
    { x: 0, y: 0 },
    { x: 1, y: 1 },
  ]
}

/** Flat at `value` across the whole domain. Not valid as a falloff — see below. */
export function constant(value: number): ControlPoint[] {
  const y = clamp01(value)
  return [
    { x: 0, y },
    { x: 1, y },
  ]
}

/** `steps` flat treads, each a hair short of the next so the riser is vertical. */
export function stepped(steps = 4): ControlPoint[] {
  const n = Math.max(2, Math.round(steps))
  const out: ControlPoint[] = []
  for (let i = 0; i < n; i++) {
    const y = i / (n - 1)
    out.push({ x: i / n, y })
    out.push({ x: (i + 1) / n - 0.001, y })
  }
  out.push({ x: 1, y: 1 })
  return out
}

/** Slow start. */
export function easeIn(): ControlPoint[] {
  return sampled((t) => t * t)
}

/** Slow finish. */
export function easeOut(): ControlPoint[] {
  return sampled((t) => 1 - (1 - t) * (1 - t))
}

/** Smoothstep — slow at both ends. The usual province falloff. */
export function easeInOut(): ControlPoint[] {
  return sampled((t) => t * t * (3 - 2 * t))
}

/**
 * Turn a profile preset into a falloff one: same shape, other way up.
 *
 * Tonio: _"0,1 to 1,0 works fine as well if we just think the other way up and
 * it makes the same presets work for both."_ Which collapses what were two
 * preset sets into one — and the endpoint rule survives the flip for free,
 * because a preset ending at y = 1 flips to one ending at y = 0, which is
 * exactly what a falloff must do.
 *
 * `constant` is the one that does not survive, and that is the same exclusion
 * already documented: a constant flipped is still constant, so it still carries
 * weight at the boundary. The rule holds; the preset was always the exception.
 *
 * A y-flip cannot reorder x, so no re-sort is needed.
 */
export function flipCurve(points: ControlPoint[]): ControlPoint[] {
  return points.map((p) => ({ x: p.x, y: clamp01(1 - p.y) }))
}

/**
 * The default falloff — a straight line from `(0,1)` to `(1,0)`.
 *
 * The mirror of `linear()`'s role for a profile: the plainest thing that obeys
 * the rules, so what you start from says nothing you did not ask for. It was a
 * smoothstep first, which quietly asserted a taste before the author had
 * expressed one.
 */
export function falloffDefault(): ControlPoint[] {
  return flipCurve(linear())
}

/**
 * A regular n-gon — exactly `n` VERTICES, not a sampled curve.
 *
 * Tonio: _"a hexagon would just be a hexagon. A circle becomes the expensive
 * shape but even that could be a 16 gon and work quite well."_ Which is the
 * right data: six numbers for a hexagon, and every vertex a thing you can grab.
 *
 * This only works because `polygonExtent` casts a ray at the real straight EDGE
 * rather than interpolating radius against angle. A straight edge is NOT linear
 * in polar — interpolating in (θ, r) bows it inward — and an earlier version
 * papered over that by sampling each edge twelve times, which is a lot of points
 * to carry to avoid doing the intersection once.
 *
 * Circumradius 1, so the VERTICES reach the declared extent and the edges fall
 * inside it. (Inscribing is equally defensible and makes "extent" stop matching
 * the number you set, which is worse.)
 */
export function ngon(sides: number): ControlPoint[] {
  const n = Math.max(3, Math.round(sides))
  const out: ControlPoint[] = []
  for (let i = 0; i < n; i++) out.push({ x: i / n, y: 1 })
  return out
}

/**
 * Every direction alike. A circle is the EXPENSIVE footprint — there is no
 * finite polygon that is one — so it is a 16-gon, which at province scale is
 * indistinguishable and costs sixteen numbers.
 */
export function circle(): ControlPoint[] {
  return ngon(16)
}

/**
 * Where the ray at `theta` (a fraction of a turn) leaves the polygon.
 *
 * Real ray/segment intersection against the straight edge, so the flats are
 * flat. Interpolating r against θ instead — the obvious thing, and what a
 * piecewise-linear curve does — bows every edge inward toward the centre, which
 * reads as the model failing rather than as an interpolation choice.
 *
 * Returns 0 for a degenerate polygon rather than throwing: a caller sampling a
 * terrain grid wants a number, and `moveVertex` already makes degeneracy
 * unreachable through the editor.
 */
export function polygonExtent(vertices: ControlPoint[], theta: number): number {
  const n = vertices.length
  if (n < 3) return 0
  const a = (((theta % 1) + 1) % 1) * Math.PI * 2
  const dx = Math.cos(a)
  const dy = Math.sin(a)
  const pt = (v: ControlPoint) => ({
    x: v.y * Math.cos(v.x * Math.PI * 2),
    y: v.y * Math.sin(v.x * Math.PI * 2),
  })
  for (let i = 0; i < n; i++) {
    const p = pt(vertices[i])
    const q = pt(vertices[(i + 1) % n])
    const ex = q.x - p.x
    const ey = q.y - p.y
    // Cross the segment with the ray direction; parallel means this edge is not
    // the one the ray leaves through.
    const denom = ex * dy - ey * dx
    if (Math.abs(denom) < 1e-12) continue
    const t = -(p.x * dy - p.y * dx) / denom
    if (t < -1e-9 || t > 1 + 1e-9) continue
    const r = (p.x + t * ex) * dx + (p.y + t * ey) * dy
    // Forward along the ray only — the opposite edge also intersects the LINE.
    if (r > 0) return r
  }
  return 0
}

/**
 * A jittered n-gon — a footprint that does not look drawn with a compass.
 *
 * Deterministic: the jitter comes from a hash of the vertex index, not
 * `Math.random`, so the same call gives the same coastline every time. A
 * footprint that reshuffled on reload would make a province unreproducible,
 * which is the one thing a seeded world cannot have.
 *
 * Angles are jittered by less than a third of a gap, so the order — and with it
 * the star-shape — survives without needing a clamp.
 */
export function messyNgon(sides = 16, jitter = 0.22, seed = 1): ControlPoint[] {
  const n = Math.max(3, Math.round(sides))
  const hash = (i: number): number => {
    const v = Math.sin((i + 1) * 127.1 + seed * 311.7) * 43758.5453
    return v - Math.floor(v)
  }
  const out: ControlPoint[] = []
  for (let i = 0; i < n; i++) {
    const wobble = (hash(i) - 0.5) * (1 / n) * 0.6
    out.push({
      x: (((i / n + wobble) % 1) + 1) % 1,
      y: clamp01(1 - hash(i + n) * jitter),
    })
  }
  out.sort((a, b) => a.x - b.x)
  return out
}

/* --------------------------------------------------- named landform presets */

/**
 * Trench, continental shelf, then mountains — the shape of a real coastline in
 * one levels map.
 *
 * The lowest ground drops away steeply (a trench), most of the middle is a broad
 * FLAT (the shelf, which is why so much of the world is shallow sea and coastal
 * plain), and only the top of the range climbs hard. It is the flat middle that
 * makes it read as Earth rather than as noise: real terrain spends most of its
 * area near sea level.
 */
export function shelfAndMountains(): ControlPoint[] {
  return [
    { x: 0, y: 0 },
    { x: 0.06, y: 0.05 },
    { x: 0.11, y: 0.28 },
    { x: 0.46, y: 0.34 },
    { x: 0.66, y: 0.46 },
    { x: 0.85, y: 0.7 },
    { x: 1, y: 1 },
  ]
}

/**
 * Old desert: a plain at one level, then terraces above it.
 *
 * Weathering cuts flats and leaves risers, so an old landscape is stepped where
 * a young one is smooth — and everything below the plain is a single level
 * because it has had time to fill in.
 */
export function desertTerraces(): ControlPoint[] {
  return [
    { x: 0, y: 0.3 },
    { x: 0.4, y: 0.32 },
    { x: 0.4, y: 0.45 },
    { x: 0.62, y: 0.47 },
    { x: 0.62, y: 0.6 },
    { x: 0.82, y: 0.62 },
    { x: 0.82, y: 0.74 },
    { x: 1, y: 0.78 },
  ]
}

/** Full weight across most of the province, then off — the flat-topped case. */
export function plateauFalloff(hold = 0.62): ControlPoint[] {
  return [
    { x: 0, y: 1 },
    { x: Math.min(0.9, hold), y: 1 },
    { x: 1, y: 0 },
  ]
}

/** Eased both ends — the province melts into its surroundings. */
export function smoothEdge(): ControlPoint[] {
  return flipCurve(easeInOut())
}

/** Holds, then drops in the last tenth: a rim, a crater wall, a quarry. */
export function abruptEdge(hold = 0.88): ControlPoint[] {
  return [
    { x: 0, y: 1 },
    { x: Math.min(0.97, hold), y: 0.94 },
    { x: 1, y: 0 },
  ]
}

/** A crater/volcano rim — non-monotonic, and the reason monotonicity is not enforced. */
export function rim(peak = 0.7, height = 1): ControlPoint[] {
  const p = Math.min(0.95, Math.max(0.05, peak))
  return normalizeCurve(
    [
      { x: 0, y: 0.15 },
      { x: p * 0.5, y: 0.3 },
      { x: p, y: clamp01(height) },
      { x: (1 + p) / 2, y: 0.35 },
      { x: 1, y: 0 },
    ],
    'falloff'
  )
}

export interface CurvePreset {
  name: string
  /** Which kinds it is OFFERED for — see `constant`. */
  kinds: CurveKind[]
  build: () => ControlPoint[]
}

/**
 * The presets, tagged with where they are valid.
 *
 * **`constant` is a profile preset only**, and that falls straight out of the
 * endpoint rule: a constant falloff is weight 1 at the boundary, which is
 * exactly the step the `f(1) = 0` pin exists to prevent. Offering it for a
 * falloff would mean offering a seam.
 */
/*
Named for what they ARE, not for their maths.

"ease in-out" tells you the shape of a graph; "smooth edge" tells you what the
province will look like, which is the question actually being asked. The maths
names survive as building blocks (`easeInOut` and friends are still exported) —
they just are not what a menu should offer.
*/
export const curvePresets: CurvePreset[] = [
  // ---- shape: a levels map from height sample to height
  { name: 'no change', kinds: ['profile'], build: linear },
  { name: 'shelf + mountains', kinds: ['profile'], build: shelfAndMountains },
  { name: 'desert terraces', kinds: ['profile'], build: desertTerraces },
  { name: 'terraced', kinds: ['profile'], build: () => stepped(4) },
  { name: 'flatten', kinds: ['profile'], build: () => constant(0.6) },
  { name: 'steepen', kinds: ['profile'], build: easeIn },
  { name: 'soften', kinds: ['profile'], build: easeOut },
  // ---- falloff: weight by distance. All reach 0 at the boundary.
  { name: 'plateau', kinds: ['falloff'], build: () => plateauFalloff() },
  { name: 'smooth edge', kinds: ['falloff'], build: smoothEdge },
  { name: 'abrupt edge', kinds: ['falloff'], build: () => abruptEdge() },
  { name: 'linear', kinds: ['falloff'], build: falloffDefault },
  // A rim is a shape you want against DISTANCE and has no reading as a remap.
  { name: 'rim', kinds: ['falloff'], build: () => rim() },
  // ---- footprint
  { name: 'circle', kinds: ['radial'], build: circle },
  { name: 'messy circle', kinds: ['radial'], build: () => messyNgon() },
  { name: 'square', kinds: ['radial'], build: () => ngon(4) },
  { name: 'hexagon', kinds: ['radial'], build: () => ngon(6) },
  { name: 'triangle', kinds: ['radial'], build: () => ngon(3) },
  { name: 'octagon', kinds: ['radial'], build: () => ngon(8) },
]

/**
 * The presets valid for a kind — what a menu should offer.
 *
 * A falloff gets the SAME set, flipped: one definition of "ease in", presented
 * the way round that kind needs it.
 */
export function presetsFor(kind: CurveKind): CurvePreset[] {
  // No flipping any more: the falloff presets are written the way round they are
  // used, because "smooth edge" is a thing in its own right rather than
  // "ease in-out, upside down". `flipCurve` remains for callers converting a
  // profile they already have.
  return curvePresets.filter((p) => p.kinds.includes(kind))
}

/** The default curve for a kind — the plainest thing that obeys its rules. */
export function defaultCurve(kind: CurveKind): ControlPoint[] {
  if (kind === 'falloff') return falloffDefault()
  if (kind === 'radial') return circle()
  return linear()
}

// --- Serialisation: the interop contract with tosijs-3d-ensemble (#61) -------

/**
 * Decimal places a curve is rounded to when committed.
 *
 * A curve lives in a file an author commits and diffs, so raw drag floats mean
 * nudging one control rewrites every number with new noise. Four places is
 * below UI resolution, kills the noise, and stays readable — agreed with
 * ensemble, who diff these by hand.
 */
export const CURVE_PRECISION = 4

const round4 = (v: number): number => {
  const f = 10 ** CURVE_PRECISION
  // `+0` normalises -0, which serialises as `-0` and shows up as a spurious diff.
  return Math.round(v * f) / f + 0
}

/**
 * The two accepted serialised forms.
 *
 * A bare array is the DEFAULT and the one to write: the domain (`kind`) is a
 * property of the FIELD, so declaring it in the schema keeps one truth instead
 * of copying it into every instance.
 *
 * The wrapper is accepted, not required, for a case ensemble raised that we
 * could not have known: their format has open bags (`Piece.meta`, `Zone.values`)
 * where no schema applies, so a bare curve landing there loses its domain
 * entirely. Reading both costs nothing and gives a schema-less context
 * something self-describing to put there.
 */
export type SerializedCurve =
  | ControlPoint[]
  | { kind?: CurveKind; points: ControlPoint[] }

/** A validation issue, in the shape ensemble's `validate()` collects. */
export interface CurveIssue {
  severity: 'error' | 'warning'
  code: string
  message: string
  /**
   * JSON Pointer RELATIVE to the value handed in — `/3/x` for a bare array,
   * `/points/3/x` for a wrapper, `''` for the value as a whole.
   *
   * Relative because the consumer knows where the field lives and we do not.
   * Ensemble prefixes with the field's own path; that join is theirs to do, and
   * this way `validateCurve` needs to know nothing about ensembles.
   */
  path: string
}

const isPointish = (p: unknown): p is ControlPoint =>
  p != null && typeof p === 'object' && 'x' in p && 'y' in p

/**
 * Read either accepted form into `{kind, points}`, normalized.
 *
 * NEVER throws. A curve from a newer tosijs-3d, or from an open bag, or from a
 * hand-edit that went wrong, degrades to something usable — because the
 * alternative is an editor that will not open the document you need to fix.
 */
export function readCurve(
  value: SerializedCurve | null | undefined,
  fallbackKind: CurveKind = 'profile'
): { kind: CurveKind; points: ControlPoint[] } {
  if (value == null) return { kind: fallbackKind, points: [] }
  if (Array.isArray(value)) {
    return { kind: fallbackKind, points: normalizeCurve(value, fallbackKind) }
  }
  const kind = value.kind ?? fallbackKind
  const points = Array.isArray(value.points) ? value.points : []
  return { kind, points: normalizeCurve(points, kind) }
}

/**
 * The canonical bytes for a curve: sorted, rounded, and nothing else on it.
 *
 * Same input, same output — so an author who nudges one control gets a diff
 * touching one line rather than the whole curve. Key order is fixed by
 * construction (`{x, y}`), since JS preserves insertion order and
 * `JSON.stringify` follows it.
 */
export function canonicalCurve(
  points: ControlPoint[],
  kind: CurveKind = 'profile'
): ControlPoint[] {
  return normalizeCurve(points, kind).map((p) => ({
    x: round4(p.x),
    y: round4(p.y),
  }))
}

/**
 * Report what is wrong with a serialised curve without throwing or fixing it.
 *
 * `error` means it is not a curve; `warning` means it is one we had to
 * interpret. Both are reportable and neither is fatal — an editor shows
 * everything and keeps working, which is only possible if reading and
 * validating are separate operations.
 */
export function validateCurve(
  value: unknown,
  kind: CurveKind = 'profile'
): CurveIssue[] {
  const issues: CurveIssue[] = []
  let points: unknown
  let prefix: string

  if (Array.isArray(value)) {
    points = value
    prefix = ''
  } else if (value != null && typeof value === 'object' && 'points' in value) {
    points = (value as { points: unknown }).points
    prefix = '/points'
    const k = (value as { kind?: unknown }).kind
    if (k != null && !['profile', 'falloff', 'radial'].includes(k as string)) {
      issues.push({
        severity: 'warning',
        code: 'curve/unknown-kind',
        message: `Unknown curve kind ${JSON.stringify(k)}; treated as ${kind}.`,
        path: '/kind',
      })
    }
  } else {
    return [
      {
        severity: 'error',
        code: 'curve/not-a-curve',
        message:
          'Expected an array of {x, y} points, or an object with a `points` array.',
        path: '',
      },
    ]
  }

  if (!Array.isArray(points)) {
    return [
      {
        severity: 'error',
        code: 'curve/points-not-an-array',
        message: '`points` must be an array of {x, y}.',
        path: prefix,
      },
    ]
  }

  if (points.length < 2) {
    issues.push({
      severity: 'warning',
      code: 'curve/too-few-points',
      message: `A curve needs at least 2 points; found ${points.length}. Ends will be supplied.`,
      path: prefix,
    })
  }

  let lastX = -Infinity
  let unsorted = false
  points.forEach((p, i) => {
    const at = `${prefix}/${i}`
    if (!isPointish(p)) {
      issues.push({
        severity: 'error',
        code: 'curve/bad-point',
        message: 'Each point must be an object with numeric x and y.',
        path: at,
      })
      return
    }
    for (const axis of ['x', 'y'] as const) {
      const v = (p as any)[axis]
      if (typeof v !== 'number' || !Number.isFinite(v)) {
        issues.push({
          severity: 'error',
          code: 'curve/bad-number',
          message: `${axis} must be a finite number.`,
          path: `${at}/${axis}`,
        })
      } else if (v < 0 || v > 1) {
        // A warning, not an error: the value is readable, we just had to clamp
        // it. Refusing to load a document over a 1.0001 helps nobody.
        issues.push({
          severity: 'warning',
          code: 'curve/out-of-range',
          message: `${axis} is ${v}; curves are [0,1] and this will be clamped.`,
          path: `${at}/${axis}`,
        })
      }
    }
    const px = (p as any).x
    if (typeof px === 'number' && px < lastX) unsorted = true
    if (typeof px === 'number') lastX = px
    for (const key of Object.keys(p as object)) {
      if (key !== 'x' && key !== 'y') {
        issues.push({
          severity: 'warning',
          code: 'curve/unknown-field',
          message: `Unknown field ${JSON.stringify(key)} will be ignored.`,
          path: `${at}/${key}`,
        })
      }
    }
  })

  if (unsorted) {
    issues.push({
      severity: 'warning',
      code: 'curve/unsorted',
      message: 'Points are not ascending in x; they will be sorted on read.',
      path: prefix,
    })
  }
  return issues
}

/**
 * A JSON Schema fragment for a curve field, for a panel generated from schema.
 *
 * `x-widget` dispatches to the editor; `x-curve-kind` says what x MEANS, which
 * the numbers cannot. Separate keys rather than a compound token, because a
 * compound would put string-parsing in the one place that dispatches on the
 * widget — ensemble's convention, and their other adjuncts (`x-unit`,
 * `x-labels`) follow it.
 */
export function curveSchema(
  kind: CurveKind = 'profile',
  extra: Record<string, unknown> = {}
): Record<string, unknown> {
  return {
    type: 'array',
    items: {
      type: 'object',
      properties: { x: { type: 'number' }, y: { type: 'number' } },
      required: ['x', 'y'],
    },
    'x-widget': 'curve',
    'x-curve-kind': kind,
    ...extra,
  }
}
